"use client";

import type { AudioSample } from "@mw/shared/payload-types";

import { Button } from "@ui/components/core/Button";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useDebounceValue } from "usehooks-ts";

const PlayIcon = ({ className }: { className?: string }) => (
  <svg viewBox='0 0 24 24' className={className} fill='currentColor' aria-hidden='true'>
    <path d='M9.244 2.368C7.414 1.184 5 2.497 5 4.676v14.648c0 2.18 2.414 3.493 4.244 2.309l11.318-7.324c1.675-1.084 1.675-3.534 0-4.618z' />
  </svg>
);

const PauseIcon = ({ className }: { className?: string }) => (
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 11' fill='none' aria-hidden='true' className={className}>
    <path
      d='M0 0.583333C0 0.261167 0.261167 0 0.583333 0H2.91667C3.23883 0 3.5 0.261167 3.5 0.583333V9.91667C3.5 10.2388 3.23883 10.5 2.91667 10.5H0.583333C0.261167 10.5 0 10.2388 0 9.91667V0.583333Z'
      fill='white'
    />
    <path
      d='M5.83333 0.583333C5.83333 0.261167 6.0945 0 6.41667 0H8.75C9.07216 0 9.33333 0.261167 9.33333 0.583333V9.91667C9.33333 10.2388 9.07216 10.5 8.75 10.5H6.41667C6.0945 10.5 5.83333 10.2388 5.83333 9.91667V0.583333Z'
      fill='white'
    />
  </svg>
);

const PreviousIcon = ({ className }: { className?: string }) => (
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10' fill='none' aria-hidden='true' className={className}>
    <path
      d='M7.53589 9.13928C8.30345 9.65044 9.3335 9.10159 9.3335 8.17746V1.15761C9.3335 0.233481 8.30345 -0.315374 7.53589 0.19578L2.26534 3.70571C1.57843 4.16316 1.57844 5.17191 2.26534 5.62936L7.53589 9.13928Z'
      fill='black'
    />
    <path
      d='M0.000162125 8.75087C0.000162125 9.07303 0.261329 9.3342 0.583495 9.3342C0.905662 9.3342 1.16683 9.07303 1.16683 8.75087V0.584199C1.16683 0.262033 0.905662 0.000865936 0.583495 0.000865936C0.261329 0.000865936 0.000162125 0.262033 0.000162125 0.584199V8.75087Z'
      fill='black'
    />
  </svg>
);

const NextIcon = ({ className }: { className?: string }) => (
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14' fill='none' aria-hidden='true' className={className}>
    <g>
      <path
        d='M4.1311 2.52832C3.36355 2.01717 2.3335 2.56602 2.3335 3.49014V10.51C2.3335 11.4341 3.36354 11.983 4.1311 11.4718L9.40165 7.96189C10.0886 7.50445 10.0886 6.49569 9.40165 6.03825L4.1311 2.52832Z'
        fill='black'
      />
      <path
        d='M11.6668 2.91674C11.6668 2.59457 11.4057 2.3334 11.0835 2.3334C10.7613 2.3334 10.5002 2.59457 10.5002 2.91674V11.0834C10.5002 11.4056 10.7613 11.6667 11.0835 11.6667C11.4057 11.6667 11.6668 11.4056 11.6668 11.0834V2.91674Z'
        fill='black'
      />
    </g>
  </svg>
);

interface FloatingMusicWidgetProps {
  currentTrack: AudioSample | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

export function FloatingMusicWidget({
  currentTrack,
  isPlaying,
  onPlayPause,
  onPrevious,
  onNext,
}: FloatingMusicWidgetProps) {
  const [debouncedIsPlaying] = useDebounceValue(isPlaying, 100);
  const t = useTranslations("ClientKeys.Globals");

  if (!currentTrack) return null;

  return (
    <div className='tw-pointer-events-none tw-fixed tw-bottom-4 tw-left-0 tw-z-50 tw-flex tw-w-full tw-justify-center tw-px-1'>
      <m.div
        className='tw-pointer-events-auto tw-flex tw-items-center tw-gap-2 tw-p-2 tw-w-full tw-max-w-80 tw-rounded-full'
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        style={{
          minWidth: "fit-content",
          border: "0.5px solid rgba(0, 0, 0, 0.10)",
          background: "rgba(245, 242, 239, 0.80)",
          boxShadow: "0 6px 16px 0 rgba(78, 50, 23, 0.04)",
          backdropFilter: "blur(30px)",
        }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 40,
        }}
      >
        {/* Play/Pause Button */}
        <Button
          variant='primary'
          size='pill-lg'
          onClick={onPlayPause}
          aria-label={isPlaying ? t("pause") : t("play")}
          className='tw-flex-shrink-0 tw-text-white'
        >
          {debouncedIsPlaying ? <PauseIcon className='tw-size-3' /> : <PlayIcon className='tw-size-3' />}
        </Button>

        {/* Track Info - Fixed width to prevent layout shift */}
        <div className='tw-flex tw-flex-col tw-justify-center tw-flex-1 tw-w-32 tw-text-left tw-space-y-2'>
          <p className='tw-text-black tw-type-base'>
            <span className='tw-truncate tw-block'>{currentTrack.title || "Untitled"}</span>
          </p>
          {currentTrack.subtitle && (
            <p className='tw-text-gray-500 tw-type-xs'>
              <span className='tw-truncate tw-block'>{currentTrack.subtitle}</span>
            </p>
          )}
        </div>

        {/* Controls */}
        <div className='tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0'>
          <Button
            variant='ghost'
            size='pill-md'
            onClick={onPrevious}
            aria-label={t("previous")}
            className='tw-flex-shrink-0'
          >
            <PreviousIcon className='tw-size-2.5' />
          </Button>

          <Button variant='ghost' size='pill-md' onClick={onNext} aria-label={t("next")} className='tw-flex-shrink-0'>
            <NextIcon className='tw-size-3.5' />
          </Button>
        </div>
      </m.div>
    </div>
  );
}
